import React, { useState, useRef, useEffect } from 'react';
import axiosInstance from '../axiosConfig';
import { Toaster, toast } from 'react-hot-toast';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import './LotCompletionVerification.css'; // Import the new CSS

function LotCompletionVerification() {
  const [itemName, setItemName] = useState('');
  const [masterData, setMasterData] = useState([]);
  const [formData, setFormData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lotData, setLotData] = useState(null);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  
  const [pendingList, setPendingList] = useState([]);
  const [isListLoading, setIsListLoading] = useState(false);


  let totalShortage = 0;
  
  const supervisorTotals = {
    MONU: { rejected: 0, repair: 0, limit: 0.7 },
    SONU: { rejected: 0, repair: 0, limit: 0.7 },
    ANWAR: { rejected: 0, repair: 0, limit: 0.05 }
  };

  const getSupervisor = (particular) => {
    if (['Wrinkle Problem', 'Beading', 'Belt Cutting', 'Joint Belt Dama', 'Five hole'].includes(particular)) return 'MONU';
    if (['Colour', 'Side spray', 'Emboss'].includes(particular)) return 'SONU';
    if (['Stitching'].includes(particular)) return 'ANWAR';
    return null;
  };

  if (formData) {
    for (const key in formData) {
      if (formData[key].checked) {
        const rej = Number(formData[key].rejected || 0);
        const rep = Number(formData[key].repair || 0);
        totalShortage += rej + rep;

        const supervisor = getSupervisor(formData[key].particular);
        if (supervisor) {
          supervisorTotals[supervisor].rejected += rej;
          supervisorTotals[supervisor].repair += rep;
        }
      }
    }
  }
  const givenQty = lotData ? Number(lotData.TotalQuantity) : 0;
  let hasSupervisorExcess = false;
  
  // Calculate percentages
  const getPercentage = (rej) => givenQty > 0 ? ((rej / givenQty) * 100).toFixed(2) : 0;
  
  const itemNameRef = useRef(null);

  const toggleSidebar = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };

  const fetchMasterData = async () => {
    try {
      const res = await axiosInstance.get('/api/wip/lot-completion/master-data');
      setMasterData(res.data);
    } catch (err) {
      console.error('Error fetching master data:', err);
      toast.error('Failed to load master data');
    }
  };

  const fetchPendingList = async () => {
    setIsListLoading(true);
    try {
      const res = await axiosInstance.get('/api/wip/lot-completion/pending-list');
      if (res.data.success) {
        setPendingList(res.data.data);
      }
    } catch (err) {
      console.error('Error fetching pending list:', err);
      toast.error('Failed to load pending list');
    } finally {
      setIsListLoading(false);
    }
  };

  useEffect(() => {
    fetchMasterData();
    fetchPendingList();
  }, []);

  const fetchItemDetails = async (searchItemName) => {
    if (!searchItemName) return;

    setIsLoading(true);
    setLotData(null);
    setFormData(null);
    try {
      // 1. Fetch Item details
      const res = await axiosInstance.get(`/api/wip/lot-completion/search-item?itemName=${searchItemName}`);
      
      if (!res.data.success || !res.data.data || res.data.data.length === 0) {
        toast.error('Item not found');
        setIsLoading(false);
        return;
      }

      const itemData = res.data.data[0];
      setLotData(itemData);

      const formState = {};
      masterData.forEach(item => {
        let defaultDept = item.Department;
        if (item.Particular === 'Wrinkle Problem') {
          defaultDept = 'gluing';
        }
        
        formState[item.Id] = {
          checked: false,
          rejected: '',
          repair: '',
          department: defaultDept,
          tpu: item.Tpu,
          rejectAcceptanceLimit: item.RejectAcceptanceLimit,
          particular: item.Particular
        };
      });

      if (itemData.existingProblems && itemData.existingProblems.length > 0) {
        itemData.existingProblems.forEach(p => {
          const matchedMaster = masterData.find(m => m.Particular === p.Particular);
          if (matchedMaster && formState[matchedMaster.Id]) {
            formState[matchedMaster.Id].checked = true;
            formState[matchedMaster.Id].rejected = p.RejectedQuantity || '';
            formState[matchedMaster.Id].repair = p.RepairQuantity || '';
          }
        });
      }
      setFormData(formState);
      
    } catch (err) {
      if (err.response && err.response.status === 404) {
        toast.error('Item not found');
      } else {
        toast.error('Error fetching data');
      }
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    if(e) e.preventDefault();
    if (!itemName) {
      toast.error('Please enter an Item Name / Lot Number');
      return;
    }
    fetchItemDetails(itemName);
  };

  const handleSelectLot = (item) => {
    setItemName(item.ItemName);
    fetchItemDetails(item.ItemName);
  };

  const handleBackToList = () => {
    setLotData(null);
    setFormData(null);
    setItemName('');
    fetchPendingList(); // Refresh the list
  };

  const handleCheckboxChange = (id) => {
    setFormData(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        checked: !prev[id].checked
      }
    }));
  };

  const handleInputChange = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Filter out only checked problems
    const selectedProblems = [];
    let totalInput = 0;

    for (const key in formData) {
      if (formData[key].checked) {
        const p = formData[key];
        
        const rejQty = Number(p.rejected || 0);
        const repQty = Number(p.repair || 0);

        selectedProblems.push({
          particular: p.particular,
          materialType: '',
          department: p.department,
          supervisorName: getSupervisor(p.particular),
          actualQuantity: 0,
          rejectedQuantity: rejQty,
          repairQuantity: repQty,
          rejectAcceptancePercentage: p.rejectAcceptanceLimit
        });
      }
    }

    if (selectedProblems.length === 0) {
      toast.error('Please select at least one problem.');
      return;
    }

    const missingSupervisors = Object.keys(supervisorTotals).filter(sup => {
      return !selectedProblems.some(p => p.supervisorName === sup);
    });

    if (missingSupervisors.length > 0) {
      toast.error(`Please verify and select at least one problem (even if 0) for: ${missingSupervisors.join(', ')}`);
      return;
    }

    const supWithExcess = Object.keys(supervisorTotals).find(sup => {
      return (supervisorTotals[sup].rejected + supervisorTotals[sup].repair) > givenQty;
    });

    if (supWithExcess) {
      toast.error(`Error: ${supWithExcess}'s total shortage exceeds Given Quantity (${givenQty}).`);
      return;
    }

    const isFirstTime = !lotData.existingProblems || lotData.existingProblems.length === 0;
    
    if (isFirstTime) {
      const supWithUnused = Object.keys(supervisorTotals).find(sup => {
        return (supervisorTotals[sup].rejected + supervisorTotals[sup].repair) !== givenQty;
      });

      if (supWithUnused) {
        const remaining = givenQty - (supervisorTotals[supWithUnused].rejected + supervisorTotals[supWithUnused].repair);
        toast.error(`Error: ${supWithUnused} ka pura Given Quantity (${givenQty}) allocate nahi hua hai. Remaining: ${remaining}`);
        return;
      }
    }

    try {
      setIsLoading(true);
      
      const currentUser = JSON.parse(localStorage.getItem('user')) || {};
      const creatorName = currentUser.Name || currentUser.EmployeeID || 'Admin';
      
      await axiosInstance.post('/api/wip/lot-completion/submit', {
        itemName: lotData.ItemName,
        givenQuantity: lotData.TotalQuantity,
        problems: selectedProblems,
        createdBy: creatorName
      });
      toast.success('Data submitted successfully!');
      
      // Reset form
      setItemName('');
      setLotData(null);
      setFormData(null);
      if(itemNameRef.current) itemNameRef.current.focus();

    } catch (err) {
      console.error(err);
      toast.error('Failed to submit data');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="d-flex dashboard">
      <div className={isSidebarVisible ? "sidebar-container" : "sidebar-hidden"}>
        <Sidebar />
      </div>
      <div className="flex-grow-1" style={{ backgroundColor: '#f8fafc' }}>
        <Header toggleSidebar={toggleSidebar} isSidebarVisible={isSidebarVisible} />
        <main className="lot-verification-container">
          <div className="container-fluid">
            <Toaster position="top-right" />
            
            <div className="search-card mb-4">
              <div className="search-header d-flex justify-content-between align-items-center">
                <h4 className="mb-0"><i className="bi bi-shield-check me-2"></i>WIP Lot Completion Verification</h4>
                {lotData && (
                  <button className="btn btn-light btn-sm fw-bold text-primary" onClick={handleBackToList}>
                    <i className="bi bi-arrow-left me-1"></i> Back to List
                  </button>
                )}
              </div>
              
              {/* HIDDEN SEARCH BAR
              <div className="card-body p-4">
                <form onSubmit={handleSearch}>
                  <div className="row g-3 align-items-end">
                    <div className="col-md-8 col-lg-6">
                      <label htmlFor="itemName" className="form-label text-muted fw-semibold small text-uppercase">
                        Search Lot Number / Item Name
                      </label>
                      <div className="input-group search-input-group">
                        <span className="input-group-text bg-white border-end-0 text-muted">
                          <i className="bi bi-search"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control border-start-0 ps-0"
                          id="itemName"
                          placeholder="Enter Item Name or Lot Number"
                          value={itemName}
                          onChange={(e) => setItemName(e.target.value)}
                          ref={itemNameRef}
                          required
                        />
                        <button 
                          className="btn search-btn px-4" 
                          type="submit"
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <span><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Searching...</span>
                          ) : (
                            'Search'
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
              */}
            </div>

          {!lotData && (
            <div className="table-card fade-in-up">
              <h5 className="table-header-title mb-0">
                <i className="bi bi-list-task text-primary me-2"></i> Pending Lots
              </h5>
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0 custom-table">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '10%' }} className="text-center">Sr No.</th>
                      <th style={{ width: '50%' }}>Item Name / Lot Number</th>
                      <th style={{ width: '20%' }} className="text-center">Total Quantity</th>
                      <th style={{ width: '20%' }} className="text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isListLoading ? (
                      <tr>
                        <td colSpan="4" className="text-center py-5">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <p className="text-muted mt-2">Loading pending lots...</p>
                        </td>
                      </tr>
                    ) : pendingList.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center py-5 text-muted">
                          <i className="bi bi-inbox fs-2 d-block mb-2"></i>
                          No pending lots found.
                        </td>
                      </tr>
                    ) : (
                      pendingList.map((item, index) => (
                        <tr key={index}>
                          <td className="text-center text-muted fw-bold">{index + 1}</td>
                          <td className="fw-semibold">{item.ItemName}</td>
                          <td className="text-center">
                            <span className="badge bg-secondary rounded-pill px-3 py-2">{item.TotalQuantity}</span>
                          </td>
                          <td className="text-center">
                            <button 
                              className="btn btn-sm btn-outline-primary fw-bold px-3"
                              onClick={() => handleSelectLot(item)}
                              disabled={isLoading}
                            >
                              <i className="bi bi-pencil-square me-1"></i> Verify
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {lotData && (
            <div className="fade-in-up">
              <div className="info-banner mb-3">
                <div>
                  <strong>Item Name:</strong> <span className="val me-4">{lotData.ItemName}</span>
                </div>
                <div>
                  <strong>Ready Qty (Given Qty):</strong> <span className="val badge bg-primary text-white rounded-pill px-3 py-2 fs-6">{lotData.TotalQuantity}</span>
                </div>
              </div>

              {formData && totalShortage > 0 && (
                <div className="alert alert-secondary mb-4 shadow-sm border-0" style={{ borderLeft: '5px solid #6c757d' }}>
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                      <h6 className="mb-1"><i className="bi bi-info-circle me-2"></i><strong>Supervisor Margins & Status</strong></h6>
                      <div className="text-dark small">
                        <span>Monitor each supervisor's Rejected Percentage and Remaining Quantity independently.</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="card border-0 bg-white bg-opacity-75">
                    <div className="card-body py-3 px-3">
                      <div className="row g-3">
                        {Object.keys(supervisorTotals).map(sup => {
                          const percentage = getPercentage(supervisorTotals[sup].rejected);
                          const isPctExceeded = parseFloat(percentage) > supervisorTotals[sup].limit;
                          const supShortage = supervisorTotals[sup].rejected + supervisorTotals[sup].repair;
                          const supRemaining = givenQty - supShortage;
                          const isQtyExcess = supRemaining < 0;
                          
                          if (isQtyExcess) hasSupervisorExcess = true;

                          return (
                            <div key={sup} className="col-12 col-md-4">
                              <div className={`border rounded p-3 h-100 ${isPctExceeded || isQtyExcess ? 'border-danger bg-danger bg-opacity-10' : 'border-success bg-success bg-opacity-10'}`}>
                                <div className="d-flex justify-content-between align-items-center mb-2 border-bottom pb-2">
                                  <div className="fw-bold fs-6">{sup}</div>
                                  <div className={`small fw-bold ${isQtyExcess ? 'text-danger' : 'text-success'}`}>
                                    {isQtyExcess ? 'Excess: ' : 'Rem Qty: '}{Math.abs(supRemaining)}
                                  </div>
                                </div>
                                <div className="d-flex justify-content-between align-items-end mt-2">
                                  <div>
                                    <div className="small text-muted mb-1" style={{ fontSize: '0.75rem' }}>Rejected Percentage</div>
                                    <div className={`fs-5 fw-bold ${isPctExceeded ? 'text-danger' : 'text-success'}`} style={{ lineHeight: '1' }}>
                                      {percentage}%
                                    </div>
                                    <div className="small text-muted mt-1" style={{ fontSize: '0.7rem' }}>Limit: {supervisorTotals[sup].limit}%</div>
                                  </div>
                                  <div className="text-end">
                                    <div className="small fw-semibold text-dark">Rej: <span className="fs-6">{supervisorTotals[sup].rejected}</span></div>
                                    <div className="small fw-semibold text-dark">Rep: <span className="fs-6">{supervisorTotals[sup].repair}</span></div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {formData && (
                <form onSubmit={handleSubmit} className="table-card">
                  <h5 className="table-header-title">
                    <i className="bi bi-clipboard2-data text-primary"></i> Defects & Problems Registration
                  </h5>
                  <div className="table-responsive">
                    <table className="custom-table table table-hover align-middle mb-0">
                      <thead>
                        <tr>
                          <th className="text-center" style={{ width: '60px' }}>Select</th>
                          <th className="text-center" style={{ width: '70px' }}>Sr No.</th>
                          <th>Particular (Problem)</th>
                          <th>Department</th>
                          <th className="text-center">Acceptance Limit</th>
                          <th className="text-center">Rejected</th>
                          <th className="text-center">Repair</th>
                          <th className="text-center">Percentage</th>
                        </tr>
                      </thead>
                      <tbody>
                        {masterData.map((item, index) => {
                          const state = formData[item.Id];
                          if (!state) return null;
                          
                          const rejectedQtyForPercentage = Number(state.rejected || 0);
                          const percentage = givenQty > 0 ? ((rejectedQtyForPercentage / givenQty) * 100).toFixed(2) : 0;
                          const resolvedDept = state.department;
                          const isCompanyExpense = resolvedDept === 'COMPANY EXPENSE' || resolvedDept === 'COMPANY MISTAKE';
                          const isExceeding = !isCompanyExpense && item.RejectAcceptanceLimit !== null && parseFloat(percentage) > item.RejectAcceptanceLimit;

                          return (
                            <React.Fragment key={item.Id}>
                              <tr className={state.checked ? 'selected' : ''}>
                                <td className="text-center">
                                  <input
                                    type="checkbox"
                                    className="custom-checkbox"
                                    checked={state.checked}
                                    onChange={() => handleCheckboxChange(item.Id)}
                                  />
                                </td>
                                <td className="text-center text-muted fw-medium">{index + 1}</td>
                                <td>{item.Particular}</td>
                                <td>
                                  {item.Particular === 'Wrinkle Problem' ? (
                                    <select
                                      className="form-select form-select-sm"
                                      value={state.department}
                                      onChange={(e) => handleInputChange(item.Id, 'department', e.target.value)}
                                      disabled={!state.checked}
                                    >
                                      <option value="gluing">gluing</option>
                                      <option value="pasting">pasting</option>
                                      <option value="excruding">excruding</option>
                                    </select>
                                  ) : (
                                    <span className="text-muted fw-medium">{state.department || '-'}</span>
                                  )}
                                </td>
                                <td className="text-center">
                                  {item.RejectAcceptanceLimit ? (
                                    <span className="badge bg-light text-dark border border-secondary">{item.RejectAcceptanceLimit}%</span>
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </td>
                                <td className="text-center">
                                  <input
                                    type="number"
                                    className="qty-input"
                                    placeholder="0"
                                    value={state.rejected}
                                    onChange={(e) => handleInputChange(item.Id, 'rejected', e.target.value)}
                                    disabled={!state.checked}
                                  />
                                </td>
                                <td className="text-center">
                                  <input
                                    type="number"
                                    className="qty-input"
                                    placeholder="0"
                                    value={state.repair}
                                    onChange={(e) => handleInputChange(item.Id, 'repair', e.target.value)}
                                    disabled={!state.checked}
                                  />
                                </td>
                                <td className="text-center fw-bold">
                                  {state.checked ? (
                                    <span className={isExceeding ? 'text-danger' : 'text-success'}>
                                      {percentage}%
                                    </span>
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </td>
                              </tr>
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="row justify-content-end mt-4">
                    <div className="col-md-4 col-lg-3 mt-3 mt-md-0 text-md-end">
                      <button type="submit" className="submit-btn m-0 w-100" disabled={isLoading || hasSupervisorExcess}>
                        {isLoading ? (
                          <span><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Submitting...</span>
                        ) : (
                          <span><i className="bi bi-check-circle me-2"></i>Submit Verification</span>
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </div>
          )}
          
          {/*
          {!lotData && !isLoading && (
            <div className="empty-state">
              <i className="bi bi-box-seam"></i>
              <h5>No Item Selected</h5>
              <p>Search for a Lot Number to begin verification</p>
            </div>
          )}
          */}
          
          </div>
        </main>
      </div>
    </div>
  );
}

export default LotCompletionVerification;
